import { defaultPost } from '@bikemap/js/utility'
import { FEATURES_LIST } from '@bikemap/js/integrations/premium'
import { context, modals, notifications, gettext } from '@bikemap/js/services'
import { subscriptionPause } from '@bikemap/js/api'
import { LANGUAGE } from '@bikemap/js/settings'

function handleCancelSubscriptionSubmit(e, modal) {
    e.preventDefault()
    const body = new FormData(modal.content)
    if (!body.has('cancel_reason')) {
        notifications.show({
            message: gettext('A cancel reason must be given.'),
            icon: 'i-warning-sign',
            type: 'danger',
        })
        return
    }
    if (modal.body.querySelector('#cancel_reason_other').checked && !body.get('cancel_reason_other_details')) {
        notifications.show({
            message: gettext('Details must be given if you select other reason.'),
            icon: 'i-warning-sign',
            type: 'danger',
        })
        return
    }
    modal.setLoading()
    defaultPost(context.page('cancelSubscriptionUrl'), body)
        .then(({ success, url }) => {
            if (success && url) {
                window.location = url
            } else {
                location.reload()
            }
        })
        .catch(res => notifications.error(res.statusText))
}

function showCancelSubscriptionConfirmModal(e, modal) {
    if (!modals.show('cancel-subscription-confirm')) {
        const reasons = [
            gettext("I'm not using it enough."),
            gettext("Didn't meet my expectations."),
            gettext('Too complicated.'),
            gettext('Too expensive.'),
            gettext("I'm having too many technical issues."),
        ]
        const choices = reasons.reduce((string, reason) => {
            return string + '<label><input type="radio" name="cancel_reason" value="' + reason + '" %}"> ' + reason + '</label><br>'
        }, '')

        modals.show('cancel-subscription-confirm', {
            title: gettext('Cancel Subscription'),
            lead: gettext('It wouldn\'t be the same without you. Could you tell us why you want to cancel?'),
            body: (
                '<div class="mb text-left">' +
                choices +
                '<label><input type="radio" name="cancel_reason" value="' + gettext('Other') + '" id="cancel_reason_other"> ' +
                    gettext('Other') +
                '</label>' +
            '</div>' +
            '<div class="form-field hidden" id="cancel_reason_other_container">' +
                '<textarea name="cancel_reason_other_details" placeholder="' + gettext('Please provide further details.') + '"></textarea>' +
            '</div>'
            ),
            buttons: [
                { content: gettext('Stay Premium'), style: 'primary', action: 'close' },
                { content: gettext('Cancel Membership'), action: 'submit' },
            ],
            onChange: (e, modal) => {
                const otherCheckbox = modal.body.querySelector('#cancel_reason_other')
                const otherContainer = modal.body.querySelector('#cancel_reason_other_container')
                if (otherCheckbox.checked) {
                    otherContainer.classList.remove('hidden')
                } else {
                    otherContainer.classList.add('hidden')
                }
            },
            onSubmit: handleCancelSubscriptionSubmit,
        })
    }
}

function showCancelSubscriptionModal() {
    modals.show('cancel-subscription', {
        title: gettext('Cancel Subscription'),
        lead: gettext('Are you sure? By downgrading your account, you will lose your downloaded Offline maps and access to all Bikemap Premium features:'),
        body: FEATURES_LIST,
        buttons: [
            { content: gettext('Stay Premium'), style: 'primary', action: 'close' },
            { content: gettext('Downgrade'), action: 'submit' },
        ],
        onSubmit: showCancelSubscriptionConfirmModal,
    })
}

async function handlePauseSubscriptionSubmit(e, modal) {
    e.preventDefault()
    modal.setLoading()
    try {
        const pauseUntil = await subscriptionPause.start()
        if (pauseUntil) {
            location.reload()
        } else {
            throw Error
        }
    } catch (error) {
        console.error(error)
        notifications.error(gettext('Membership could not be paused'))
        modals.close()
    }
}

function showPauseSubscriptionModal() {
    modals.show('pause-subscription', {
        title: gettext('Currently not cycling?'),
        lead: gettext('You can pause your membership and simply reactivate Bikemap Premium at a later point in time.'),
        body: (
            '<ul class="list-check text-left inline-block mb">' +
                '<li><i class="i-check"></i>' + gettext('Pause once for up to 2 months.') + '</li>' +
                '<li><i class="i-check"></i>' + gettext('No charge while your membership is paused.') + '</li>' +
                '<li><i class="i-check"></i>' + gettext('Reactivate Bikemap Premium anytime.') + '</li>' +
            '</ul>'
        ),
        buttons: [
            { content: gettext('Yes, pause membership'), style: 'premium', type: 'submit' },
            { content: gettext('No, please cancel'), action: showCancelSubscriptionModal },
        ],
        onSubmit: handlePauseSubscriptionSubmit,
    })
}

async function handleEndPause(e) {
    e.preventDefault()
    try {
        const validUntil = await subscriptionPause.end()
        if (validUntil) {
            location.reload()
        } else {
            throw Error
        }
    } catch (error) {
        console.error(error)
        notifications.error(gettext('Membership could not be reactivated'))
    }
}

function displayPausedInfo(pausedUntil) {
    const infoBox = document.getElementById('subscription-info-box')
    infoBox.classList.remove('orange')
    infoBox.classList.add('secondary')
    infoBox.querySelector('.subscription-status').classList.add('hidden')
    infoBox.querySelector('.subscription-pause').classList.remove('hidden')
    const actions = infoBox.querySelector('.subscription-actions')
    actions.innerHTML = '<p class="text-success">'
    actions.innerHTML += gettext('Back to cycling? You can reactivate your Bikemap Premium membership now to get access to all exclusive features again.')
    actions.innerHTML += '</p>'

    const endPauseBtn = document.createElement('button')
    endPauseBtn.setAttribute('class', 'btn btn-success')
    endPauseBtn.textContent = gettext('Reactivate Membership')
    endPauseBtn.addEventListener('click', handleEndPause)
    actions.append(endPauseBtn)

    const reactivationInfo = document.querySelector('.reactivation-info')
    reactivationInfo.classList.remove('hidden')
    const pausedUntilString = pausedUntil.format((LANGUAGE === 'de') ? 'D. MMM Y' : 'MMM D Y')
    reactivationInfo.querySelector('span').textContent = pausedUntilString
}

export async function initCancelSubscription() {
    const cancelSubscriptionLink = document.getElementById('cancel-subscription-link')
    if (cancelSubscriptionLink) {
        // Cancel modal will be shown by default
        const defaultHandler = e => {
            e.preventDefault()
            showCancelSubscriptionModal()
        }
        cancelSubscriptionLink.addEventListener('click', defaultHandler)

        const { isPaused, pauseAvailable, pausedUntil } = await subscriptionPause.status()

        if (!isPaused && pauseAvailable) {
            // Show pause modal first
            cancelSubscriptionLink.removeEventListener('click', defaultHandler)
            cancelSubscriptionLink.addEventListener('click', e => {
                e.preventDefault()
                showPauseSubscriptionModal()
            })
        }

        if (isPaused && pausedUntil) {
            displayPausedInfo(pausedUntil)
        }
    }
}
